import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ticketingActions } from "../../redux/actions/ticketingActions";
import {
  getTicketingIsNotificationEmailSaving,
  getTicketingLoginEmail,
  getTicketingNotificationEmail,
  getTicketingNotificationEmailError,
  getTicketingNotificationEmailVerified,
} from "../../redux/selectors/ticketingSelectors";
import * as styles from "./NotificationEmailSection.module.css";
import { useTicketingStrings } from "./ticketingStrings";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const NotificationEmailSection = () => {
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const notificationEmail = useSelector(getTicketingNotificationEmail);
  const loginEmail = useSelector(getTicketingLoginEmail);
  const isSaving = useSelector(getTicketingIsNotificationEmailSaving);
  const serverError = useSelector(getTicketingNotificationEmailError);
  const verified = useSelector(getTicketingNotificationEmailVerified);

  const isEffectivelyVerified =
    verified ||
    (!!loginEmail &&
      !!notificationEmail &&
      loginEmail.toLowerCase() === notificationEmail.toLowerCase());

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [validationError, setValidationError] = useState<string | undefined>();

  const handleEdit = useCallback(() => {
    setEditValue(notificationEmail ?? "");
    setValidationError(undefined);
    setIsEditing(true);
  }, [notificationEmail]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setValidationError(undefined);
  }, []);

  const handleSave = useCallback(() => {
    const trimmed = editValue.trim();
    if (!trimmed || !emailRegex.test(trimmed)) {
      setValidationError(strings.ticketInvalidEmailMsg);
      return;
    }
    setValidationError(undefined);
    dispatch(ticketingActions.updateNotificationEmail.start(trimmed));
    setIsEditing(false);
  }, [editValue, dispatch, strings.ticketInvalidEmailMsg]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSave();
      } else if (e.key === "Escape") {
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  if (!notificationEmail) {
    return null;
  }

  return (
    <div className={styles.notificationSection}>
      <span className={styles.notificationLabel}>{strings.ticketNotificationEmailLabel}</span>
      {isEditing ? (
        <>
          <input
            type="email"
            className={styles.notificationInput}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label={strings.ticketNotificationEmailAriaLabel}
          />
          <button className={styles.notificationButton} onClick={handleSave} disabled={isSaving}>
            {isSaving ? strings.ticketSavingLabel : strings.ticketSaveLabel}
          </button>
          <button className={styles.notificationButton} onClick={handleCancel} disabled={isSaving}>
            {strings.ticketCancelLabel}
          </button>
        </>
      ) : (
        <>
          <span className={styles.notificationEmail}>{notificationEmail}</span>
          {isEffectivelyVerified ? (
            <span className={styles.verifiedBadge}>{strings.ticketVerifiedBadge}</span>
          ) : (
            <span className={styles.unverifiedBadge}>{strings.ticketNotVerifiedBadge}</span>
          )}
          <button className={styles.notificationButton} onClick={handleEdit}>
            {strings.ticketChangeLabel}
          </button>
        </>
      )}
      {(validationError || serverError) && (
        <span className={styles.notificationError}>{validationError ?? serverError}</span>
      )}
    </div>
  );
};
